import KeywordFilter from "components/filters/Filters/KeywordFilter";
import ListTemplate from "components/ListTemplate";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React, { useState } from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { mapFilterValuesToInstruction, mapResponseToFilterValues } from "./helpers";
import RightSideNavigation from "components/RightSideNavigation/RightSideNavigation";
import classNames from "classnames";
import CultureFlag from "components/Culture/CultureFlag";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import Tag from "components/Tag/Tag";
import UserToClientLifecycleFilter from "components/filters/Filters/UserToClientLifecycleFilter";
import FreefieldProfileFilter from "components/filters/Filters/FreefieldProfileFilter";
import ArtistFilter from "components/filters/Filters/ArtistFilter";
import LimitedList from "components/LimitedList";
import { PhosphorWarningCircle } from "components/Atoms/PhosphorIcon";
import { useLoaderData } from "react-router";
import { NewUserForExternalModal } from "../NewUserForExternalModal";

const PermissionCircle = ({ hasPermission }: { hasPermission: boolean }) => {
  return <i className={classNames("icon-circle", { red: !hasPermission, green: hasPermission })}></i>
}

const UserForExternalAdminListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.ExternalUserListFrontModel>();
  const [isNewOpen, setIsNewOpen] = useState(false);

  const isActiveUser = (item: server.dto.UserForExternalAdminForList) => item.lifeCycle === "active";

  return <>
    <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <ListTemplate
          title={viewModel.pageTitle}
          filterSet="userForExternalAdmin_List"
          defaultFilters={viewModel.defaultFilters}
          initialInstruction={viewModel.initialInstruction}
          fetchFunction={window.CoreApi.UserForExternalAdmin.Paginated}
          getId={item => item.userToClientId}
          onNew={() => setIsNewOpen(true)}
          layout={{
            type: "table",
            columns: [
              {
                header: RESX.GeneralLabels.Name, key: "name", render: item =>
                  <UserWithAvatar user={item.user} nameHref={routes.externalUser_Detail(item.userToClientId).absolute}>
                    {item.artists.length > 0 && <div className="s1_small">
                      <LimitedList
                        items={item.artists}
                        renderItem={artist => artist}
                        renderExcess={nr => ` (+${nr})`}
                        limit={5}
                        delimiter=", "
                      />
                    </div>}
                    {item.artists.length === 0 && <div className="s1_small s1_red">{RESX.SoUser.NoAccessToArtists}</div>}
                    {!isActiveUser(item) && <div><Tag color="red" label={RESX.SoUser.Deactivated} /></div>}
                  </UserWithAvatar>
              },
              { header: RESX.Buttons.Add, align: "center", key: "canAddShows", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canAddShows} /> },
              { header: site.currentEnvironment.functionGroups.promoter.singular, align: "center", key: "canReadPromoterDetails", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canReadPromoterDetails} /> },
              { header: RESX.Financial.Costs, align: "center", key: "canReadCosts", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canReadCosts} /> },
              { header: RESX.Financial.Sales, align: "center", key: "canReadSales", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canReadSales} /> },
              { header: RESX.Guestlist.resxGuestlist, align: "center", key: "canAccessGuestlist", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canAccessGuestlist} /> },
              { header: RESX.Webform.Webforms, align: "center", key: "canConfigureBookingForms", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canConfigureBookingForms} /> },

              {
                header: RESX.Feed.iCalendarFeeds,
                key: "iCalendarFeeds",
                align: "center",
                render: item => {
                  if (!isActiveUser(item) || item.iCalendarFeedCount === 0) return <></>
                  return item.iCalendarFeedCount;
                }
              },
              {
                header: RESX.FreeField.FreeFields,
                key: "freeFields",
                render: item => {
                  if (!isActiveUser(item)) return <></>
                  if (item.freeFieldProfile == null) return <PhosphorWarningCircle iconColor="error" iconSize="large" />
                  return <>{item.freeFieldProfile.title}</>
                }
              },
              { header: "", key: "cultureFlag", render: item => isActiveUser(item) && <CultureFlag lcid={item.user.culture} /> },
            ]
          }}
          filters={<>
            <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
            <UserToClientLifecycleFilter />
            <FreefieldProfileFilter environment="external" />
            <ArtistFilter securityScope="allForClient" />
          </>}
          functions={{
            mapResponseToFilterValues,
            mapFilterValuesToInstruction
          }}
        />
      </TwoColumnLayout.LeftColumn>
      <RightSideNavigation
        title={desktopNavigation.rightSideNavigation.title}
        menuItems={desktopNavigation.rightSideNavigation.items}
        currentPage={desktopNavigation.currentPage}
      />
    </TwoColumnLayout>
    <NewUserForExternalModal
      isOpen={isNewOpen}
      onClose={() => setIsNewOpen(false)}
    />
  </>
};


export default UserForExternalAdminListApp;